import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges  } from '@angular/core';

@Component({
  selector: 'app-accordion',
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.scss'],
})
export class AccordionComponent implements OnInit {
  @Input() roadmaps = [];
  @Input() filteredRoadmaps: any;
  @Input() showCheckbox: boolean;
  @Input() showCount: boolean;
  @Input() showStatusBox: boolean;
  @Input() textColor: string;
  @Input() bgColor: string;
  @Input() collapseAll : boolean = false;
  @Output() accordionHeader = new EventEmitter<string>();
  @Output() roadmapHistory = new EventEmitter<{ event: Event, item: any }>();
  @Output() toggleCheckbox = new EventEmitter<string>();
  @Output() toggleAllCheckbox = new EventEmitter<string>();
  @Output() includeYesCompliantChange = new EventEmitter<any>();
  @Output() disabledIncludeYesCompliantTap = new EventEmitter<any>();
  @Output() onReorderRoadmaps = new EventEmitter<any[]>();
  @Output() collapseAllChange = new EventEmitter<boolean>();
  constructor() { }

  ngOnInit() {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.collapseAll === true) {
      this.toggleAllAccordions(this.collapseAll);
    }    

    if (changes.roadmaps) {
      this.roadmaps = changes.roadmaps.currentValue;
      this.roadmaps.forEach(item => this.syncRoadmapSelectionState(item, false));
    }
  }

   drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.roadmaps, event.previousIndex, event.currentIndex);
    this.roadmaps.forEach((roadmap, index) => {
      roadmap.order_no = index + 1; // Update order based on new index
    });
    this.onReorderRoadmaps.emit(this.roadmaps);
   }

  toggleAllAccordions(shouldCollapse: boolean): void {
    this.roadmaps.forEach(item => item.active = !shouldCollapse);
  }

  /**
   * Toggles accordion
   * 
   * @param item item
   */
  public toggleAccordion(item): void {
    if (!item.active) {
      this.roadmaps.forEach((i) => {
        if (i.active) {
          i.active = false
        }
      });
    }
    item.active = !item.active;
    if (item.active) {
      this.collapseAllChange.emit(false);
    }
    this.accordionHeader.emit(item);
  }

  public onAccordionArrowClick(item, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.toggleAccordion(item);
  }

  public onAccordionHeaderEnter(event: KeyboardEvent, item): void {
    event.preventDefault();
    this.toggleAccordion(item);

    const header = event.currentTarget as HTMLElement | null;
    setTimeout(() => {
      header?.focus();
    });
  }

  public onCheckboxEnter(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const checkbox = event.target as HTMLInputElement;
    if (checkbox) {
      checkbox.click();
      setTimeout(() => {
        checkbox.focus();
      });
    }
  }

  public onYesCompliantToggleKeydown(item, event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (item?.isYesCompliantToggleDisabled) {
      return;
    }

    const toggleWrapper = event.currentTarget as HTMLElement | null;
    const toggleElement = toggleWrapper?.querySelector('ion-toggle') as HTMLElement | null;

    if (toggleElement) {
      toggleElement.click();
    }

    setTimeout(() => {
      toggleWrapper?.focus();
    });
  }

  public toggleIncludeYesCompliant(item, event: CustomEvent | KeyboardEvent): void {
    event.stopPropagation();

    const checked = (event as CustomEvent)?.detail?.checked;
    if (typeof checked === 'boolean') {
      item.includeYesCompliantSection = checked;
    }

    this.includeYesCompliantChange.emit(item);
  }

  public handleIncludeYesCompliantTap(item, event: Event): void {
    event.stopPropagation();
    if (item?.isYesCompliantToggleDisabled) {
      this.disabledIncludeYesCompliantTap.emit(item);
    }
  }

  public getRoadmapHistory(event, item) {
    item.active = false;
    this.roadmapHistory.emit({event, item});
  }

  /**
   * Toggles all selections
   * 
   * @param item item
   * @param event event
   */
  public toggleAllSelections(item, event: Event): void {
    event.stopPropagation();
    const selected = item.selected;
    const hasLoadedBodyItems = Array.isArray(item?.bodyItems) && item.bodyItems.length > 0;

    if (hasLoadedBodyItems) {
      item.bodyItems.forEach(subItem => this.setSubItemsSelection(subItem, selected));
      this.syncRoadmapSelectionState(item);
    } else {
      item.selected = selected;
      item.indeterminate = false;

      if (!selected && item.includeYesCompliantSection) {
        item.includeYesCompliantSection = false;
        this.includeYesCompliantChange.emit(item);
      }

      item.isYesCompliantToggleDisabled = !selected;
    }

    this.toggleAllCheckbox.emit(item)
  }

  /**
   * Updates header checkbox
   * 
   * @param item item
   */
  public updateHeaderCheckbox(item): void {
    this.syncRoadmapSelectionState(item);
    this.toggleCheckbox.emit(item);
  }

  /**
   * Sets sub items selection
   * 
   * @param item item
   * @param selected selected
   */
  private setSubItemsSelection(item, selected: boolean): void {
    item.selected = selected;
    item.indeterminate = false;
    if (item.subItems) {
      item.subItems.forEach(subItem => this.setSubItemsSelection(subItem, selected));
    }
  }

  /**
   * Toggles body item selection
   * 
   * @param subItem subItem
   * @param parentItem parentItem
   */
  public toggleBodyItemSelection(subItem, parentItem): void {
    this.setSubItemsSelection(subItem, subItem.selected);
    this.updateHeaderCheckbox(parentItem);
  }

  public trackByRoadmap(index: number, item: any): any {
    return item?.id ?? item?.roadmap_id ?? item?.roadmap_name ?? index;
  }

  public trackByBodyItem(index: number, item: any): any {
    return item?.id ?? item?.question_id ?? item?.text ?? index;
  }

  public trackByNestedBodyItem(index: number, item: any): any {
    return item?.id ?? item?.question_id ?? item?.text ?? index;
  }

  public isTextTruncated(element: HTMLElement): boolean {
    return element?.scrollWidth > element?.clientWidth;
  }

  private syncRoadmapSelectionState(item, emitToggleChange: boolean = true): void {
    const counts = item?.bodyItems?.reduce((total, bodyItem) => {
      const bodyItemCounts = this.syncNestedSelectionState(bodyItem);
      return {
        selectedCount: total.selectedCount + bodyItemCounts.selectedCount,
        totalCount: total.totalCount + bodyItemCounts.totalCount,
      };
    }, { selectedCount: 0, totalCount: 0 }) || { selectedCount: 0, totalCount: 0 };

    item.selected = counts.totalCount > 0 && counts.selectedCount === counts.totalCount;
    item.indeterminate = counts.selectedCount > 0 && counts.selectedCount < counts.totalCount;

    if (counts.selectedCount === 0) {
      if (item.includeYesCompliantSection) {
        item.includeYesCompliantSection = false;
        if (emitToggleChange) {
          this.includeYesCompliantChange.emit(item);
        }
      }
      item.isYesCompliantToggleDisabled = true;
      return;
    }

    item.isYesCompliantToggleDisabled = false;
  }

  private syncNestedSelectionState(item): { selectedCount: number; totalCount: number } {
    if (!item?.subItems?.length) {
      item.indeterminate = false;
      return {
        selectedCount: item?.selected ? 1 : 0,
        totalCount: 1,
      };
    }

    const counts = item.subItems.reduce((total, subItem) => {
      const nestedCounts = this.syncNestedSelectionState(subItem);
      return {
        selectedCount: total.selectedCount + nestedCounts.selectedCount,
        totalCount: total.totalCount + nestedCounts.totalCount,
      };
    }, { selectedCount: 0, totalCount: 0 });

    item.selected = counts.totalCount > 0 && counts.selectedCount === counts.totalCount;
    item.indeterminate = counts.selectedCount > 0 && counts.selectedCount < counts.totalCount;

    return counts;
  }

  /**
   * Determines whether roadmaps empty is
   * 
   * @returns true if roadmaps empty 
   */
  public isRoadmapsEmpty(): boolean {
    return (
      this.roadmaps.length === 0 ||
      this.roadmaps.every(item => typeof item === 'string' && item.trim() === '')
    );
  }

}